import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getSingalClassroomData } from "../../../utils/classrooms";
import { setNavbarParams } from "../../../store/navbar";
import {
  getStudentEndPoints,
  MustIncludedSidebarItems,
} from "../../../constants/sidebar";
import { setLinks, showLinks } from "../../../store/sidebar";
import { ListGroup } from "react-bootstrap";
import { getAssessmentByCourseId } from "../../../utils/assessments";
import { getIELTSExamsByCourseId } from "../../../utils/ieltsApi";
import EmptyScreen from "../../../components/EmptyScreen";
import Loader from "../../../components/Loader"; // Import the Loader component
import {
  getUserInformation,
  getUserPermission,
  getUserRole,
} from "../../../store/userRole";
import { useSelector } from "react-redux";
import DetailsList from "./DetailsList";
import { extractModulesInMentionedOrder } from "../../../utils/validations";

function FullLengthExams({ isMobileView, hasValidSubscription }) {
  const { id: classroomId } = useParams();
  const queryParams = new URLSearchParams(window.location.search);
  const notifyExam = queryParams.get("assesmentId");
  const roleOfTheUser = useSelector(getUserRole);
  const [assessments, setAssessments] = useState([]);
  const [modules, setModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const storeDispatch = useDispatch();
  const navigate = useNavigate();
  const isFirstRef = useRef(false);

  const rolePermissions = useSelector(getUserPermission);
  const userInformation = useSelector(getUserInformation);
  const [createdBy, setCreatedBy] = useState("");
  const [focusExam, setFocusExam] = useState("");
  const [ieltsTypeTab, setIeltsTypeTab] = useState("all");

  useEffect(() => {
    if (
      notifyExam?.length &&
      isFirstRef.current === false &&
      assessments?.length
    ) {
      isFirstRef.current = true;
      setFocusExam(notifyExam);
    }
  }, [assessments, notifyExam]);

  const scrollToExam = useCallback((topicId) => {
    const topicElement = document.getElementById(topicId);
    if (topicElement) {
      topicElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  useEffect(() => {
    if (focusExam?.length) {
      scrollToExam(focusExam);
      setTimeout(() => {
        setFocusExam("");
      }, [1500]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusExam]);

  const getClassroomDetails = async () => {
    try {
      const response = await getSingalClassroomData(classroomId);
      const courseId = response?.data?.classRoom?.course?._id;
      setModules(response?.data?.classRoom?.modulesIncluded);
      setCreatedBy(response?.data?.classRoom?.createdBy);
      const hasIeltsExams = response?.data?.classRoom?.hasIeltsExams

      if (!courseId) {
        setIsLoading(false);
        return;
      }
      let assessmentsData = null
      let ieltsResponse = null
      if(hasIeltsExams) {
        ieltsResponse = await getIELTSExamsByCourseId(courseId)
      } else {
        assessmentsData = await getAssessmentByCourseId(courseId)
      }

      // Extract data from settled promises
      const regularAssessments = assessmentsData?.assessments?.length ? assessmentsData?.assessments : [];
      const ieltsExams = ieltsResponse?.exams?.length ? ieltsResponse?.exams : [];
      // Filter IELTS exams to only show active ones (with sections)
      const activeIeltsExams = ieltsExams.filter(
        (exam) => exam.sections && exam.sections.length > 0,
      );

      // Mark IELTS exams with a type identifier and transform them to match assessment structure
      const transformedIeltsExams = activeIeltsExams.map((exam) => ({
        ...exam,
        examType: "ielts",
        name: exam.title || exam.name,
        shortDescription:
          exam.description ||
          `IELTS ${(exam.type || exam.examType || "Academic").charAt(0).toUpperCase() + (exam.type || exam.examType || "Academic").slice(1)} Exam`,
      }));

      // Combine all exams
      const allExams = [...regularAssessments, ...transformedIeltsExams];
      setAssessments(allExams);
    } catch (error) {
      console.error("Error fetching classroom or assessments:", error.message);
    } finally {
      setIsLoading(false); // Set loading to false after data is fetched
    }
  };

  useEffect(() => {
    getClassroomDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    storeDispatch(setNavbarParams(`Full-Length Exams`));
    const defaultItems = [
      ...getStudentEndPoints(
        classroomId,
        roleOfTheUser,
        userInformation?._id === createdBy,
        rolePermissions,
      ),
    ];
    const sidebarItems = defaultItems.filter(
      (item) =>
        modules?.includes(item?.value) ||
        MustIncludedSidebarItems?.includes(item?.value),
    );
    const indOfItem = sidebarItems?.findIndex(
      (item) => item.value === "Full-Length Exams",
    );
    if (indOfItem !== -1) {
      sidebarItems[indOfItem].selected = true;
    }
    storeDispatch(
      setLinks(extractModulesInMentionedOrder(sidebarItems, modules)),
    );
    storeDispatch(showLinks());
  }, [
    classroomId,
    storeDispatch,
    modules,
    roleOfTheUser,
    userInformation?._id,
    createdBy,
    rolePermissions,
  ]);

  const IELTS_TABS = [
    { id: "all", label: "All Exams" },
    { id: "academic", label: "Academic" },
    { id: "general", label: "General" },
  ];

  const filteredAssessments = assessments.filter((exam) => {
    if (ieltsTypeTab === "all") return true;
    if (exam.examType === "ielts") return exam.type === ieltsTypeTab;
    return true;
  });

  const handleStartClick = (examId, examType) => {
    if (examType === "ielts") {
      // Navigate to IELTS exam details page
      navigate(
        `${
          roleOfTheUser === "Student" ? "/student" : ""
        }/myClassrooms/${classroomId}/ielts-exams/${examId}`,
      );
    } else {
      // Navigate to regular exam details page
      navigate(
        `${
          roleOfTheUser === "Student" ? "/student" : ""
        }/${classroomId}/exams/${examId}/details`,
      );
    }
  };

  return (
    <ListGroup>
      {assessments.some((exam) => exam.examType === "ielts") && (
        <div
          className="ielts-type-tabs d-flex mb-3 px-2"
          style={{ gap: "8px" }}
        >
          {IELTS_TABS.map((tab) => (
            <button
              key={tab.id}
              className={`ielts-tab-btn ${ieltsTypeTab === tab.id ? "active" : ""}`}
              onClick={() => setIeltsTypeTab(tab.id)}
              style={{
                padding: "6px 18px",
                borderRadius: "20px",
                border:
                  ieltsTypeTab === tab.id
                    ? "1px solid #4403bf"
                    : "1px solid #dee2e6",
                background: ieltsTypeTab === tab.id ? "#4403bf" : "#fff",
                color: ieltsTypeTab === tab.id ? "#fff" : "#6e7485",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      {isLoading ? (
        <Loader />
      ) : filteredAssessments.length > 0 ? (
        filteredAssessments.map((assessment, index) => (
          <DetailsList
            key={index}
            index={index}
            assessment={assessment}
            handleStartClick={handleStartClick}
            isMobileView={isMobileView}
            hasValidSubscription={hasValidSubscription}
            focusExam={focusExam}
          />
        ))
      ) : (
        <EmptyScreen heading="No Exams Found" subHeading="" />
      )}
    </ListGroup>
  );
}

export default memo(FullLengthExams);
